import * as XLSX from 'xlsx-js-style';
import type { MatGroup } from '../components/OSMaterialnoTable';
import type { SubprocessOption } from '../select/SubprocessCells';

interface ExportArgs {
  groups: MatGroup[];
  subprocesses: SubprocessOption[];
  mainIdent?: string;
  mainName?: string;
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

// A group carries data if Постапка/Инструкција is filled; an item carries data if any of
// its own fields is filled. A row (group+item pair) is dropped only when NEITHER side has data.
const groupHasData = (g: MatGroup): boolean => !!(g.procedure || g.instruction || g.manual);
const itemHasData = (it: MatGroup['items'][number]): boolean =>
  !!(it.itemId || it.ident || it.name || it.quantity);

// Placeholder for a Постапка that carries no components — keeps the item columns blank
// without making the row type nullable.
const emptyMatItem: MatGroup['items'][number] = {
  itemId: '',
  ident: '',
  name: '',
  unitCode: '',
  quantity: '',
  priority: '',
};

export function downloadOSMaterialnoExcel({
  groups,
  subprocesses,
  mainIdent = '',
  mainName = '',
}: ExportArgs) {
  const subprocessById = new Map(subprocesses.map((sp) => [sp._id, sp]));

  const data: Record<string, string>[] = [];
  groups.forEach((group) => {
    const subprocess = group.subprocessId ? subprocessById.get(group.subprocessId) : undefined;
    const processDefinition =
      subprocess && typeof subprocess.processDefinition === 'object'
        ? subprocess.processDefinition
        : null;
    const gHasData = groupHasData(group);
    const itemsWithData = group.items.filter(itemHasData);
    // A procedure with no components still belongs in the export — but exactly once.
    // Emitting group.items as-is would repeat it per placeholder row (emptyGroup() makes 3).
    const rowItems: MatGroup['items'] =
      itemsWithData.length > 0 ? itemsWithData : gHasData ? [emptyMatItem] : [];

    rowItems.forEach((item) => {
      data.push({
        'Главна шифра': mainIdent,
        'Главен назив': mainName,
        'Тип на процес': processDefinition?.name || '',
        'Подпроцес': subprocess?.name || '',
        'Реден бр. постапка': group.manual ? '' : (group.priority || ''),
        'Приоритет': item.priority || '',
        'Постапка': group.procedure || '',
        'Инструкција': group.instruction || '',
        'Шифра': item.ident || '',
        'Назив': item.name || '',
        'ЕМ': item.unitCode || '',
        'Кол. составница': item.quantity || '',
      });
    });
  });

  const headers = [
    'Главна шифра',
    'Главен назив',
    'Тип на процес',
    'Подпроцес',
    'Реден бр. постапка',
    'Приоритет',
    'Постапка',
    'Инструкција',
    'Шифра',
    'Назив',
    'ЕМ',
    'Кол. составница',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  worksheet['!cols'] = [
    { wch: 16 },
    { wch: 32 },
    { wch: 24 },
    { wch: 18 },
    { wch: 24 },
    { wch: 14 },
    { wch: 10 },
    { wch: 40 },
    { wch: 40 },
    { wch: 16 },
    { wch: 28 },
    { wch: 10 },
    { wch: 16 },
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:M1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:M1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) worksheet[cellRef].s = cellStyle;
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Материјално');
  XLSX.writeFile(
    workbook,
    `materialno_${safeFilenamePart(mainIdent)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
